import { useState } from 'react';
import type { OrcamentoResponse } from '@triade/contracts';
import { Button } from '@/components/ui/button';
import { BotaoExportar, ErroExportacao, useExportar } from '@/components/exportar';
import { useAprovarVersao, useCriarProximaVersao, useCriarVersaoComCustosAtuais, useDescartarVersao, useEnviarVersao } from '@/api/orcamentos';
import { formatarData } from '@/lib/format';
import { cn } from '@/lib/utils';
import { DialogoAcaoVersao, mensagemErroVersao, type AcaoVersao } from './dialogo-acao-versao';
import { ChipCustoEpoca, IndicadorAoVivo } from '@/components/orcamento/custo-atualizado';
import { acoesDaVersao, ROTULO_VERSAO, rotuloSeletorVersao } from './acoes';

export interface PainelVersaoProps {
  /** Detalhe do orcamento: cabecalho (situacao) e a lista `versoes` (aba Versoes). */
  detalhe: OrcamentoResponse;
  /** Versao exibida (rascunho aberto, a selecionada na URL ou a de referencia). */
  ver: OrcamentoResponse;
  podeEditar: boolean;
  /** Ha alteracoes nao gravadas no formulario: enviar e baixar o PDF esperam o salvar. */
  alteracoesPendentes: boolean;
  /** Troca a versao exibida (vai para `?versao=N`). */
  onSelecionar: (n: number) => void;
}

const ESTILO_STATUS: Record<OrcamentoResponse['versaoStatus'], string> = {
  RASCUNHO: 'border-primary text-primary',
  ENVIADA: 'border-border text-ink-body',
  APROVADA: 'border-success text-success-ink',
  DESCARTADA: 'border-border text-ink-muted line-through',
  IMPORTADA: 'border-border text-ink-secondary',
};

/**
 * Painel de versoes do detalhe: seletor, indicadores (rascunho editavel, enviada congelada, aprovada, descartada,
 * importada somente leitura), validade e selo "Vencido" (da API), acoes de versao. O sha256 do PDF fica na secao de parametros (Verificação do documento).
 * Nada aqui calcula: datas, status, vencido e hash vem do servidor.
 */
export function PainelVersao({ detalhe, ver, podeEditar, alteracoesPendentes, onSelecionar }: PainelVersaoProps) {
  const [acao, setAcao] = useState<AcaoVersao | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);
  const enviar = useEnviarVersao();
  const aprovar = useAprovarVersao();
  const descartar = useDescartarVersao();
  const criarProxima = useCriarProximaVersao();
  const criarComCustos = useCriarVersaoComCustosAtuais();
  const pdf = useExportar();

  const resumo = detalhe.versoes.find((v) => v.numero === ver.versaoNumero);
  const acoes = acoesDaVersao({
    versaoStatus: ver.versaoStatus,
    versaoNumero: ver.versaoNumero,
    situacao: detalhe.status,
    temRascunhoAberto: detalhe.versoes.some((v) => v.status === 'RASCUNHO'),
    podeEditar,
    origemApp: ver.origem === 'APP',
  });
  const identidade = { id: ver.id, n: ver.versaoNumero };
  const ocupado = enviar.isPending || aprovar.isPending || descartar.isPending || criarProxima.isPending || criarComCustos.isPending;
  // So existe com o comparativo (versao de referencia ENVIADA em aberto, dentro da validade): a API e quem decide.
  const podeNovaComCustos = acoes.criarProxima && ver.comparativoCusto !== undefined;

  async function confirmarAcao() {
    if (acao === 'enviar') await enviar.mutateAsync(identidade);
    else if (acao === 'aprovar') await aprovar.mutateAsync(identidade);
    else if (acao === 'descartar') await descartar.mutateAsync(identidade);
    else if (acao === 'custos-atuais') {
      const nova = await criarComCustos.mutateAsync({ id: ver.id });
      onSelecionar(nova.versaoNumero);
    }
    setAcao(null);
    setAviso(null);
  }

  async function proximaVersao() {
    setAviso(null);
    try {
      const nova = await criarProxima.mutateAsync({ id: ver.id });
      onSelecionar(nova.versaoNumero);
    } catch (e) {
      setAviso(mensagemErroVersao(e));
    }
  }

  return (
    <section aria-label="Versões do orçamento" data-testid="painel-versao" className="flex flex-col gap-3 rounded-2xl border border-border bg-card px-5 py-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2.5">
          <label htmlFor="o-versao" className="text-small text-ink-secondary">
            Versão
          </label>
          <select
            id="o-versao"
            aria-label="Versão"
            value={ver.versaoNumero}
            disabled={ocupado}
            onChange={(e) => onSelecionar(Number(e.target.value))}
            className="h-[34px] max-w-[320px] rounded-[10px] border border-input bg-card px-3 text-small font-medium outline-none focus:border-primary focus:shadow-focus"
          >
            {detalhe.versoes.map((v) => (
              <option key={v.id} value={v.numero}>
                {rotuloSeletorVersao(v)}
              </option>
            ))}
          </select>
          <span data-testid="numero-exibido" className="tabular text-body font-semibold">
            {ver.numeroExibido}
          </span>
          <span
            data-testid="versao-status"
            data-status={ver.versaoStatus}
            className={cn('rounded-full border bg-card px-2.5 py-0.5 text-caption font-semibold', ESTILO_STATUS[ver.versaoStatus])}
          >
            {ROTULO_VERSAO[ver.versaoStatus]}
          </span>
          {ver.vencido && (
            <span data-testid="versao-vencido" className="rounded-full bg-warning-bg px-2.5 py-0.5 text-caption font-semibold text-warning-ink">
              Vencido
            </span>
          )}
          {ver.versaoReferencia && <span className="text-caption text-ink-muted">Vale para lista, margens e Excel</span>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {acoes.enviar && (
            <Button type="button" data-testid="acao-enviar" disabled={alteracoesPendentes || ocupado} title={alteracoesPendentes ? 'Salve as alterações antes de enviar' : undefined} onClick={() => setAcao('enviar')}>
              Enviar ao cliente
            </Button>
          )}
          {acoes.criarProxima && (
            <Button type="button" variant="outline" data-testid="acao-criar-proxima" disabled={ocupado} onClick={() => void proximaVersao()}>
              Criar próxima versão
            </Button>
          )}
          {podeNovaComCustos && (
            <Button type="button" variant="outline" data-testid="acao-nova-versao-custos" disabled={ocupado} onClick={() => setAcao('custos-atuais')}>
              Gerar nova versão com custos atuais
            </Button>
          )}
          {acoes.aprovar && (
            <Button type="button" variant="outline" data-testid="acao-aprovar" disabled={ocupado} onClick={() => setAcao('aprovar')}>
              Aprovar esta versão
            </Button>
          )}
          {acoes.descartar && (
            <Button type="button" variant="ghost" data-testid="acao-descartar" disabled={alteracoesPendentes || ocupado} onClick={() => setAcao('descartar')}>
              Descartar rascunho
            </Button>
          )}
          <span title={alteracoesPendentes ? 'Salve as alterações antes de baixar o PDF: ele usa a versão gravada' : 'PDF desta versão'}>
            <BotaoExportar
              rotulo="Baixar PDF da versão"
              exportando={pdf.exportando}
              desabilitado={alteracoesPendentes}
              onClick={() => void pdf.exportar(`/orcamentos/${ver.id}/versoes/${ver.versaoNumero}/pdf`)}
            />
          </span>
        </div>
      </div>

      <ErroExportacao erro={pdf.erro} />
      {aviso && (
        <p role="alert" className="rounded-xl border border-destructive/30 bg-danger-bg px-3 py-2 text-small text-danger-ink">
          {aviso}
        </p>
      )}

      {ver.comparativoCusto && (
        <div data-testid="bloco-custo-epoca">
          <ChipCustoEpoca comparativo={ver.comparativoCusto} />
        </div>
      )}
      {ver.versaoStatus === 'RASCUNHO' && ver.aoVivo !== undefined && <IndicadorAoVivo aoVivo={ver.aoVivo} />}

      <dl className="flex flex-wrap gap-x-6 gap-y-2 text-small text-ink-secondary">
        <div>
          <dt className="inline text-ink-muted">Validade: </dt>
          <dd className="inline text-ink-body" data-testid="versao-validade">
            {ver.validaAte ? `até ${formatarData(ver.validaAte)} (${ver.validadeDias} dias)` : `${ver.validadeDias} dias, contados a partir do envio`}
          </dd>
        </div>
        {resumo?.enviadaEm && (
          <div>
            <dt className="inline text-ink-muted">Enviada em: </dt>
            <dd className="inline text-ink-body">
              {formatarData(resumo.enviadaEm)}
              {resumo.enviadaPorNome ? ` por ${resumo.enviadaPorNome}` : ''}
            </dd>
          </div>
        )}
        {resumo?.migrada && (
          <div>
            <dd className="text-ink-body">Versão anterior às versões: sem PDF congelado.</dd>
          </div>
        )}
      </dl>

      {/* key: cada acao abre uma instancia nova. Reaproveitar o dialogo deixava o overlay de um diálogo anterior por cima do conteudo (cliques bloqueados). */}
      <DialogoAcaoVersao key={acao ?? 'fechado'} acao={acao} numeroExibido={ver.numeroExibido} onConfirmar={confirmarAcao} onCancelar={() => setAcao(null)} />
    </section>
  );
}
